import { useState, useEffect } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import Loading from '@/components/Loading';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import CandidatesProfileCard from '../components/CandidatesProfileCard';
import CandidatesProfileTab from '../components/CandidatesProfileTab';
import { TAB_ITEMS, type TimelineFilter } from '../constants';
import CandidatesMypageSettingCard from '../components/CandidatesMypageSettingCard';
import CandidatesChatCard from '../components/CandidatesChatCard';
import CandidatesTimelineCard from '../components/CandidatesTimelineCard';
import CandidatesOfferCard from '../components/CandidatesOfferCard';
import useFetchCandidateProfile from '../api/useFetchCandidateProfile';
import CandidatesMatchingCard from '../components/CandidatesMatchingCard';
import CandidatesUnreadMessageBanner from '../components/CandidatesUnreadMessageBanner';
import clsx from 'clsx';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import useFetchIam from '@/features/agent/api/useFetchIam';
import NotFoundPage from '@/features/agent/errors/NotFoundPage';

type Props = {
  id: string;
};

export default function CandidatesShowTemplate({ id }: Props) {
  const {
    data: candidateProfile,
    refetch: candidateProfileRefetch,
    isLoading,
    isError,
  } = useFetchCandidateProfile(Number(id));
  const { data: iam } = useFetchIam();
  const isJobOnlyPlan = iam?.plan_type === 'job_only';
  const documentTitle = candidateProfile?.name || '求職者管理';
  useSetBreadcrumbTitle(candidateProfile?.name);
  useDocumentTitle(documentTitle);
  const isInitialLoading = isLoading && !candidateProfile;

  // TanStack Router の検索パラメータ tab を唯一の情報源としてタブ状態を管理する。
  // ヘッダー受信箱などクライアント遷移（<Link>）でも search が更新されるため、
  // 従来の window.location / popstate 依存を廃止しルーターの状態に追従する。
  const search = useSearch({ strict: false }) as { tab?: string; filter?: TimelineFilter };
  const navigate = useNavigate();

  const resolveTab = (
    tabParam: string | undefined,
    jobOnly: boolean
  ): (typeof TAB_ITEMS)[number]['value'] => {
    if (!tabParam || !TAB_ITEMS.some((item) => item.value === tabParam)) {
      return 'profile';
    }

    // job_only プランでは matching タブのコンテンツが描画されないため profile にフォールバック
    if (tabParam === 'matching' && jobOnly) {
      return 'profile';
    }

    return tabParam as (typeof TAB_ITEMS)[number]['value'];
  };

  const [tab, setTab] = useState<(typeof TAB_ITEMS)[number]['value']>(() =>
    resolveTab(search.tab, false)
  );

  const handleTabChange = (newTab: (typeof TAB_ITEMS)[number]['value']) => {
    setTab(newTab);
    // 検索パラメータをルーター経由で更新し、search の状態と同期させる（履歴は置換）
    navigate({
      to: '/agent/candidates/$candidateProfileId',
      params: { candidateProfileId: id },
      search: (prev) => ({ ...prev, tab: newTab }),
      replace: true,
    });
  };

  const handleTimelineFilterChange = (filter: TimelineFilter) => {
    navigate({
      to: '/agent/candidates/$candidateProfileId',
      params: { candidateProfileId: id },
      search: (prev) => ({ ...prev, filter: filter === 'system' ? filter : undefined }),
      replace: true,
    });
  };

  // マイページ（チャット）タブを一度開いたら未読は解消済みとみなしバナーを閉じる
  const [hasVisitedChat, setHasVisitedChat] = useState(false);
  useEffect(() => {
    if (tab === 'mypage') setHasVisitedChat(true);
  }, [tab]);
  const showUnreadBanner = (candidateProfile?.unread_message_count ?? 0) > 0 && !hasVisitedChat;

  // 検索パラメータ tab の変化（ヘッダー受信箱からのクライアント遷移・ブラウザ戻る/進む含む）や
  // iam ロード後の plan 変化に追従してタブを同期する
  useEffect(() => {
    setTab(resolveTab(search.tab, isJobOnlyPlan));
  }, [search.tab, isJobOnlyPlan]);

  if (isInitialLoading) {
    return (
      <div className="flex h-[calc(100vh-60px)] w-full flex-col items-center justify-center gap-2">
        <Loading size="small" />
        <p className="text-sm text-placeholder">求職者情報を読み込み中です…</p>
      </div>
    );
  }

  // 404（存在しない/権限外の候補者）や取得失敗時は 500 画面ではなく NotFound を表示する
  if (isError || !candidateProfile) {
    return <NotFoundPage />;
  }

  return (
    <div className="hidden-scrollbar flex h-[calc(100vh-60px)] flex-col">
      {showUnreadBanner && (
        <CandidatesUnreadMessageBanner onConfirm={() => handleTabChange('mypage')} />
      )}
      <div className="sticky z-30 bg-white pt-md">
        <CandidatesProfileTab
          candidateId={Number(id)}
          tab={tab}
          setTab={handleTabChange}
          candidateName={candidateProfile?.name || ''}
          organizationUserIds={candidateProfile?.organization_users?.map((u) => u.id) ?? []}
          currentUserId={iam?.current_user?.id}
          companyName={candidateProfile?.company_name ?? ''}
          jobPosition={candidateProfile?.job_position ?? ''}
          age={candidateProfile?.age ? String(candidateProfile?.age) : ''}
          scores={{
            suggest: candidateProfile?.counter_cache.count_of_suggest || 0,
            documentScreening: candidateProfile?.counter_cache.count_of_document_screening || 0,
            interview: candidateProfile?.counter_cache.count_of_interview || 0,
            offer: candidateProfile?.counter_cache.count_of_offer || 0,
            close: candidateProfile?.counter_cache.count_of_close || 0,
          }}
          files={candidateProfile?.document_files ?? []}
          candidateProfileRefetch={candidateProfileRefetch}
          candidateBoard={{
            id: candidateProfile?.candidate_board?.id ?? undefined,
            title: candidateProfile?.candidate_board?.title ?? undefined,
            colorType: candidateProfile?.candidate_board?.color_type ?? undefined,
          }}
        />
      </div>
      <section
        data-candidates-matching-section
        className={clsx(
          'hidden-scrollbar w-full flex-1 overflow-auto p-8',
          tab === 'timeline' && 'p-lg!',
          tab === 'matching' && !isJobOnlyPlan && 'p-0!',
          tab === 'offer' && 'p-0!'
        )}
      >
        {tab === 'profile' && (
          <CandidatesProfileCard
            candidateId={Number(id)}
            candidateName={candidateProfile?.name ?? ''}
            organizationUsers={candidateProfile?.organization_users ?? []}
            customData={candidateProfile?.custom_data ?? []}
            candidateProfileRefetch={candidateProfileRefetch}
            avatarUrl={candidateProfile?.avatar?.url ?? ''}
          />
        )}
        {tab === 'timeline' && (
          <CandidatesTimelineCard
            candidateId={Number(id)}
            filter={search.filter ?? 'all'}
            onFilterChange={handleTimelineFilterChange}
          />
        )}
        {tab === 'offer' && <CandidatesOfferCard candidateId={Number(id)} />}
        {tab === 'matching' && !isJobOnlyPlan && <CandidatesMatchingCard candidateId={id} />}
        {tab === 'mypage' && (
          <div className="flex h-full items-start gap-4">
            <div className="min-w-0 flex-1">
              <CandidatesMypageSettingCard candidateId={Number(id)} />
            </div>
            <CandidatesChatCard
              candidateId={Number(id)}
              candidateName={candidateProfile?.name ?? ''}
            />
          </div>
        )}
      </section>
    </div>
  );
}
